import { Filter, SearchInput, SelectInput, useGetList, useNavigate } from 'react-admin';
import { useState } from 'react';
import { Box, Typography, Paper, Chip, Table, TableBody, TableCell, TableHead, TableRow, Avatar, TablePagination, useMediaQuery } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { ChipColor, ProgressColor } from '@/types/chip';
import { School } from '@mui/icons-material';
import { ListPageHeader } from '@/components/ListPageHeader';
import { STATUS_COLORS_COURSE, COURSE_STATUS, DELIVERY_MODE } from './constants';

interface CourseItem {
  id: string;
  status: string;
  courseTitle: string;
  setaQualificationCode?: string;
  nqfLevel: number;
  creditValue: number;
  durationHours: number;
  deliveryMode: string;
  trainingProvider?: { providerName?: string };
}

const COURSE_FILTERS = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search course title or description" />
    <SelectInput source="status" choices={COURSE_STATUS.map(s => ({ id: s, name: s }))} alwaysOn />
    <SelectInput source="deliveryMode" choices={DELIVERY_MODE.map(d => ({ id: d, name: d }))} />
  </Filter>
);

const CourseListContent = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data: courses, isLoading } = useGetList('courses', { pagination: { page: page + 1, perPage: rowsPerPage } });
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const stats = {
    total: courses?.length ?? 0,
    active: courses?.filter((c: CourseItem) => c.status === 'Active').length ?? 0,
    pending: courses?.filter((c: CourseItem) => c.status === 'PendingAccreditation').length ?? 0,
    archived: courses?.filter((c: CourseItem) => c.status === 'Archived').length ?? 0,
  };

  const deliveryModeCounts: Record<string, number> = courses?.reduce((acc: Record<string, number>, c: CourseItem) => {
    acc[c.deliveryMode] = (acc[c.deliveryMode] || 0) + 1;
    return acc;
  }, {}) ?? {};

  const pieData = Object.entries(deliveryModeCounts).map(([label, value]) => ({ label, value }));

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <ListPageHeader
          title="Course Catalogue"
          metrics={[
            { label: 'Active', value: String(stats.active), color: 'success.main' },
            { label: 'Pending', value: String(stats.pending), color: 'warning.main' },
            { label: 'Archived', value: String(stats.archived), color: 'default' },
          ]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {courses?.map((course: CourseItem) => (
            <Paper key={course.id} sx={{ p: 2 }} onClick={() => navigate(`/courses/${course.id}/show`)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontSize: 14 }}>
                    <School sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{course.courseTitle}</Typography>
                    <Typography variant="caption" color="text.secondary">{course.setaQualificationCode || '—'}</Typography>
                  </Box>
                </Box>
                <Chip label={course.status} size="small" color={STATUS_COLORS_COURSE[course.status] as ChipColor} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Typography variant="caption" color="text.secondary">L{course.nqfLevel} · {course.creditValue} credits</Typography>
                <Typography variant="caption" color="text.secondary">·</Typography>
                <Typography variant="caption" color="text.secondary">{course.deliveryMode}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>
        {(!courses || courses.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <School sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No courses found</Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="Course Catalogue"
        metrics={[
          { label: 'Active', value: String(stats.active), color: 'success.main' },
          { label: 'Pending', value: String(stats.pending), color: 'warning.main' },
          { label: 'Archived', value: String(stats.archived), color: 'default' },
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
        ]}
        pieChartData={pieData}
        pieChartTitle="By Delivery Mode"
      />
      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Course</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Provider</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>NQF</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Credits</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Delivery</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {courses?.map((course: CourseItem) => (
              <TableRow
                key={course.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/courses/${course.id}/show`)}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 12 }}>
                      <School sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{course.courseTitle}</Typography>
                      <Typography variant="caption" color="text.secondary">{course.setaQualificationCode || '—'}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{course.trainingProvider?.providerName || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={`L${course.nqfLevel}`} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{course.creditValue}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{course.durationHours}h</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={course.deliveryMode} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Chip label={course.status} size="small" color={STATUS_COLORS_COURSE[course.status] as ChipColor} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={courses?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!courses || courses.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <School sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No courses found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export const CourseList = () => <CourseListContent />;
